import { TokenList } from '@/components/admin/reglages/token-list';
import { TokenMinted } from '@/components/admin/reglages/token-minted';
import { TokenPanel } from '@/components/admin/reglages/token-panel';
import { TokenRevokeDialog } from '@/components/admin/reglages/token-revoke-dialog';
import { Callout } from '@/components/ui/callout';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession } from '@/lib/admin-session';
import { ApiError } from '@/lib/api';
import { mintFailureSentence, revokeFailureSentence, tokenCountSentence } from '@/lib/token-labels';
import {
  apiBaseUrl,
  fetchOperatorTokens,
  mintOperatorToken,
  revokeOperatorToken,
} from '@/lib/tokens-api';
import type { OperatorToken, OperatorTokenMinted } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Jetons (spec §20.2, issue #84).
 *
 * The personal tokens of the MCP assistant: strike one on a label, read the
 * clear value ONCE, list mine with their last use, revoke after a confirmation.
 * It is the first of the two doors §20.2 now describes; the second is
 * `scripts/mint-token.mjs`, which does the same three things from a terminal.
 *
 * ## The clear token never leaves this component's state
 *
 * No `localStorage`, no URL parameter, no query cache, no log. It is held by
 * `minted` below for as long as the panel is open, and dropped when the panel
 * closes or the route unmounts. The base holds only a SHA-256 fingerprint, so
 * anything that wrote the clear value down would hold the only copy of a live
 * credential - and the only one nobody rotates (H49).
 *
 * ## A machine session reads a sentence, not a 401
 *
 * These three routes are behind the COOKIE ALONE (`routes/admin-tokens.ts`):
 * a token does not strike a token, and neither does the shared one. So a
 * session opened by the shared machine token - the e2e suites, the capture
 * scripts, the CI - cannot use this screen, and `useAdminSession().email` is
 * `null` exactly then. The tab says so in French and asks for nothing rather
 * than firing three requests it knows will be refused.
 *
 * `useRejectOn401` is deliberately NOT mounted here, and it is the only admin
 * screen that leaves it out. Its job is « the door reopened », which is right
 * when a cookie expired under a screen that the cookie opens. Here a 401 is
 * also the ordinary answer to a credential that works everywhere else, and
 * bouncing an operator to the login form after they signed in perfectly well
 * would teach them the door is broken.
 *
 * A 401 therefore reaches one of two places, and WHICH one is decided by
 * whether there is anything on the screen to lose. With nothing read and
 * nothing minted, it replaces the page - `TokensUnavailable`, worded as an
 * expiry because an address was resolved. With a list already shown, or a
 * one-time panel open, it is a BANNER over the list and the page stays: a
 * cookie that dies between two requests must not take a token that will never
 * be shown again with it.
 */

export const Route = createFileRoute('/admin/reglages/jetons')({
  component: ReglagesJetonsPage,
});

/**
 * EXPORTED for `tests/reglages-jetons.test.tsx`, and for nothing else.
 *
 * `vitest.config.ts` leaves `src/routes/**` out of the coverage REPORT, not out
 * of the suite, and what this component decides - when the minted panel
 * appears and when it is dropped, which failure lands where, what a machine
 * session sees - cannot be seen from the components below it.
 */
export function ReglagesJetonsPage() {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  /** The clear token of the last mint, or nothing. See the head of this file. */
  const [minted, setMinted] = useState<OperatorTokenMinted | null>(null);
  /** The token a confirmation is open for. */
  const [confirming, setConfirming] = useState<OperatorToken | null>(null);
  /** Moves when a mint LANDS, and on nothing else: it resets the form. */
  const [mintGeneration, setMintGeneration] = useState(0);

  const tokens = useQuery({
    queryKey: ['operator-tokens', email],
    queryFn: () => fetchOperatorTokens(),
    // A machine session has no tokens to read, and asking would be asking for
    // a refusal we can predict.
    enabled: email !== null,
    retry: false,
  });

  function reread(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['operator-tokens'] });
  }

  const mint = useMutation({
    mutationFn: (label: string) => mintOperatorToken(label),
    onSuccess: (answer) => {
      setMinted(answer);
      setMintGeneration((count) => count + 1);
    },
    onSettled: () => reread(),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeOperatorToken(id),
    onSuccess: () => setConfirming(null),
    onSettled: () => reread(),
  });

  /*
    Why the LAST read failed, whether or not the query fell to `error`.

    `tokens.error` alone is not enough, and it is the defect the banner below
    was first written with: a refetch that fails on a query which already holds
    data leaves `status` at `success`, and the failure is reported through
    `failureReason`. Read from `error` only, a cookie dying under an open
    screen was completely silent - the list simply stopped moving.
  */
  const readFailure = tokens.error ?? tokens.failureReason;
  const refused = readFailure instanceof ApiError && readFailure.status === 401;
  const list = tokens.data?.tokens;
  /*
    NOTHING to show: no list ever landed, and no token is being displayed.

    This is the whole guard, and the second half of it is the one that matters.
    A 401 on a BACKGROUND re-read - a cookie that died between two requests of
    a screen already open - used to replace the page, taking the one-time panel
    with it: the operator lost a token that will never be shown again, to a
    refusal that had nothing to do with the token they were holding. The rule
    `reglages.personas.tsx` states, applied to the one screen where throwing
    away what is on it costs something irreplaceable.
  */
  const empty = list === undefined && minted === null;

  if (email === null) return <TokensUnavailable reason="machine" />;
  if (refused && empty) return <TokensUnavailable reason="expired" />;

  if (tokens.isPending) {
    return (
      <div className="flex justify-center py-16">
        <PmsSpinner label="Chargement des jetons" />
      </div>
    );
  }

  const apiUrl = apiBaseUrl();
  /*
    The banner that replaces the list when the re-read failed, worded for WHICH
    failure it was. The 401 one names the gesture to make before anything else:
    copy what is on screen, then sign in again - in that order, because signing
    in reloads the shell and the panel goes with it.
  */
  const staleRead =
    list !== undefined && readFailure !== null && readFailure !== undefined
      ? refused
        ? 'Votre session a expiré : la liste ne se rafraîchit plus. Copiez le jeton affiché, puis reconnectez-vous.'
        : 'La relecture des jetons a échoué. Ce qui est affiché date de la dernière lecture réussie.'
      : null;

  return (
    <div className="space-y-10 pb-16">
      <header className="space-y-1">
        <h2 className="font-bold text-3xl text-bleu-nuit">Jetons</h2>
        <p className="max-w-3xl text-gray-700 text-sm">
          Un jeton personnel est ce qui ouvre l’assistant MCP du dispositif (§20) depuis Claude Code
          : le <code className="font-mono">.mcp.json</code> commité à la racine du dépôt le lit dans{' '}
          <code className="font-mono">MATTER_PAT</code> et le présente à{' '}
          <code className="font-mono">/api/mcp</code>. Il vaut votre session : ce qu’il écrit est
          signé de votre adresse, et il n’expire pas - il se révoque.
        </p>
      </header>

      {minted === null ? null : (
        <TokenMinted
          /*
            A REMOUNT per token, and not a re-render: `TokenCopyField` holds
            « Copié. » in its own state, so a second strike under the same
            mounted panel would show the first token's confirmation over a
            value nobody has copied yet.
          */
          key={minted.id}
          minted={minted}
          apiUrl={apiUrl}
          onClose={() => setMinted(null)}
        />
      )}

      <section className="space-y-4">
        <div className="space-y-1">
          <h3 className="font-bold text-bleu-nuit text-xl">Frapper un jeton</h3>
          <p className="max-w-3xl text-gray-600 text-sm">
            Un jeton par poste ou par usage : le libellé est ce qui vous permettra de révoquer le
            bon plus tard. Le jeton s’affiche une seule fois, juste après la frappe.
          </p>
        </div>
        <TokenPanel
          // A REMOUNT is how this form is reset: a landed mint clears the
          // field, a refused one keeps what was typed.
          key={`frappe-${mintGeneration}`}
          submitting={mint.isPending}
          failure={mintFailureSentence(mint.error)}
          onSubmit={(label) => mint.mutate(label)}
        />
      </section>

      <section className="space-y-4">
        <div className="flex items-baseline gap-3">
          <h3 className="font-bold text-bleu-nuit text-xl">Mes jetons</h3>
          {list === undefined ? null : (
            <span className="text-gray-600 text-sm">{tokenCountSentence(list)}</span>
          )}
        </div>

        {staleRead === null ? null : (
          <p
            role="alert"
            data-testid="jetons-relecture-echouee"
            className="text-rouge-deep text-sm"
          >
            {staleRead}
          </p>
        )}

        {list === undefined ? (
          <p role="alert" className="text-rouge-deep text-sm">
            Les jetons n’ont pas pu être lus. Rechargez l’écran.
          </p>
        ) : (
          <TokenList
            tokens={list}
            revokingId={revoke.isPending ? (revoke.variables ?? null) : null}
            onRevoke={(token) => {
              // A refusal belongs to the gesture it refused: kept, the sentence
              // of the previous revocation would greet the next confirmation.
              revoke.reset();
              setConfirming(token);
            }}
          />
        )}

        <p className="max-w-3xl text-gray-500 text-xs">
          Seuls les vôtres sont listés : un jeton frappé par quelqu’un d’autre n’apparaît pas ici et
          ne se révoque pas d’ici. Un jeton révoqué reste dans la liste, grisé.
        </p>
      </section>

      {confirming === null ? null : (
        <TokenRevokeDialog
          token={confirming}
          busy={revoke.isPending}
          // Scoped to the token this box is about: `variables` is the id the
          // mutation was called with, so a failure on one row never puts a red
          // line under the box an operator opened on another.
          error={revoke.variables === confirming.id ? revokeFailureSentence(revoke.error) : null}
          onConfirm={() => revoke.mutate(confirming.id)}
          onCancel={() => {
            // Escape reaches this handler too, so a revocation IN FLIGHT cannot
            // be dismissed: closing the box would leave the gesture running
            // with nowhere to report what it did.
            if (revoke.isPending) return;
            revoke.reset();
            setConfirming(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The tab with nothing on it, for the two callers these routes do not open
 * (§20.2, T6).
 *
 * Two reasons, and they are NOT the same fact - which is why each gets its own
 * sentence rather than one hedged between them:
 *
 * - `machine` is the shell answering `email === null`. The caller came in by
 *   the shared token, which names a deployment and never a person, so there is
 *   no address to attach a personal token to, and there never was one.
 * - `expired` is only ever reached WITH an address in hand: the shell resolved
 *   a Tahiti account and these routes still answered 401, which means the
 *   cookie died between the two requests. Hedging it with « ou le jeton
 *   partagé » would send somebody who IS signed in to go and check a
 *   credential they are not using.
 */
function TokensUnavailable({ reason }: { reason: 'machine' | 'expired' }) {
  return (
    <div className="space-y-6 pb-16">
      <header className="space-y-1">
        <h2 className="font-bold text-3xl text-bleu-nuit">Jetons</h2>
      </header>
      <Callout
        tone="info"
        title={
          reason === 'machine'
            ? 'Réservé aux sessions ouvertes par un compte Tahiti'
            : 'Votre session a expiré'
        }
        data-testid="jetons-indisponible"
      >
        {reason === 'machine' ? (
          <>
            Un jeton personnel est attaché à une adresse, et cette session a été ouverte par le
            jeton partagé des machines, qui ne nomme personne. Un jeton ne frappe pas de jeton, et
            le jeton partagé non plus. Connectez-vous avec votre compte Tahiti pour frapper, lister
            et révoquer les vôtres.
          </>
        ) : (
          <>
            Elle n’ouvre plus les jetons personnels. Reconnectez-vous avec votre compte Tahiti pour
            frapper, lister et révoquer les vôtres.
          </>
        )}
      </Callout>
      <p className="max-w-3xl text-gray-600 text-sm">
        Depuis un terminal, <code className="font-mono">make mint-token</code> fait les mêmes trois
        gestes : il demande l’adresse et le mot de passe, frappe, et affiche le jeton une fois.
      </p>
    </div>
  );
}
